import React, { useCallback, useEffect, useMemo } from 'react';
import { Chain, ChainPath } from '@ankr.com/chains-list';
import { UserEndpointToken } from 'multirpc-sdk';

import { useAppSelector } from 'store/useAppSelector';
import { selectAllPathsByChainId } from 'modules/chains/store/selectors';

import { IProjectSubchains } from '../../hooks/useProjectSubchains';
import { useIsIndeterminate } from './hooks/useIsIndeterminate';
import { useIsSelected } from './hooks/useIsSelected';

interface IUseChainProjectAccordionProps {
  chain: Chain;
  handleProjectChange: (projectToken: string) => void;
  isLoadingAddChainsToProjects?: boolean;
  onToggleAccordion: (token: UserEndpointToken) => void;
  selectedProjectsSubchains: IProjectSubchains;
  setSelectedSubchains: (
    subchains: ChainPath[],
    projectToken: UserEndpointToken,
  ) => void;
  userEndpointToken: UserEndpointToken;
}

export const useChainProjectAccordion = ({
  chain,
  handleProjectChange,
  isLoadingAddChainsToProjects,
  onToggleAccordion,
  selectedProjectsSubchains,
  setSelectedSubchains,
  userEndpointToken,
}: IUseChainProjectAccordionProps) => {
  const currentProjectSubchains = useMemo(
    () => selectedProjectsSubchains[userEndpointToken] || [],
    [selectedProjectsSubchains, userEndpointToken],
  );

  const allChainPaths = useAppSelector(state =>
    selectAllPathsByChainId(state, chain.id),
  );

  const isSelected = useIsSelected({
    allChainPaths,
    currentProjectSubchains,
    isLoadingAddChainsToProjects,
  });

  const isIndeterminate = useIsIndeterminate({
    allChainPaths,
    currentProjectSubchains,
    isLoadingAddChainsToProjects,
    isSelected,
  });

  // selected chain paths for current project accordion (adopted state for children components)
  const [selectedChainPaths, setSelectedChainPaths] = React.useState<
    ChainPath[]
  >(currentProjectSubchains);

  useEffect(() => {
    if (!isLoadingAddChainsToProjects) {
      // used for synchronising temporary states on all levels (parent to child)
      setSelectedChainPaths(currentProjectSubchains);
    }
  }, [
    currentProjectSubchains,
    isLoadingAddChainsToProjects,
    selectedProjectsSubchains,
  ]);

  useEffect(() => {
    if (!isLoadingAddChainsToProjects) {
      // used for synchronising temporary states on all levels (child to parent)
      setSelectedSubchains(selectedChainPaths, userEndpointToken);
    }
  }, [
    isLoadingAddChainsToProjects,
    selectedChainPaths,
    setSelectedSubchains,
    userEndpointToken,
  ]);

  const onProjectChange = useCallback(
    () => handleProjectChange(userEndpointToken),
    [handleProjectChange, userEndpointToken],
  );

  const onAccordionClick = useCallback(
    () => onToggleAccordion(userEndpointToken),
    [onToggleAccordion, userEndpointToken],
  );

  return {
    isSelected,
    isIndeterminate,
    onProjectChange,
    onAccordionClick,
    selectedChainPaths,
    setSelectedChainPaths,
  };
};
